import { Box, Button, CircularProgress, Dialog, DialogContent, Typography } from '@mui/material';
import React from 'react';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY } from '../../pages/BrandDashboard/brandDashboardTokens';

export interface ConfirmDialogProps {
    open: boolean;
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Red destructive style for the confirm button */
    destructive?: boolean;
    loading?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    children?: React.ReactNode;
}

export function ConfirmDialog({
    open,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    loading = false,
    onConfirm,
    onCancel,
    children,
}: ConfirmDialogProps) {
    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onCancel}
            PaperProps={{
                sx: {
                    bgcolor: '#1a1919',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 3,
                    p: 0,
                    maxWidth: 400,
                    width: '100%',
                },
            }}>
            <DialogContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: 17, md: 19 },
                        color: '#fff',
                        letterSpacing: '-0.01em',
                        mb: description ? 1 : 3,
                    }}>
                    {title}
                </Typography>
                {description && (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.5)',
                            lineHeight: 1.6,
                            mb: 3,
                        }}>
                        {description}
                    </Typography>
                )}
                {children}
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={onCancel}
                        disabled={loading}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            px: 2.5,
                            color: 'rgba(255,255,255,0.5)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
                        }}>
                        {cancelLabel}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onConfirm}
                        disabled={loading}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            px: 2.5,
                            minWidth: 80,
                            bgcolor: destructive ? '#c62828' : BRAND_LUXE_PRIMARY,
                            color: destructive ? '#fff' : '#392700',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: destructive ? '#d32f2f' : '#feb700',
                                boxShadow: 'none',
                            },
                            '&.Mui-disabled': {
                                bgcolor: destructive ? 'rgba(198,40,40,0.4)' : `${BRAND_LUXE_PRIMARY}50`,
                                color: destructive ? 'rgba(255,255,255,0.4)' : '#39270060',
                            },
                        }}>
                        {loading ? <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} /> : confirmLabel}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
